import {
  type OfferCargoProcess,
  calculateLatestOnGoingDeliveryProposalOfferCargo,
  calculateLatestValidContractOfferCargo,
  calculateOfferCargoProcessStatus,
} from "@/features/offer-cargo-process-status";
import { MobileDeliveryProposalList } from "@/pages/shipper-cargo-detail/ui/mobile/delivery-proposal-list";
import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { type Loadable, transformLoadable } from "@/shared/lib/loadable";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseActionableDialog } from "@/shared/ui/base-actionable-dialog";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseDivider } from "@/shared/ui/base-divider";
import { BaseStepper } from "@/shared/ui/base-stepper";
import { OfferCargoCargoInfo } from "@/widgets/offer-cargo-cargo-info";
import { OfferCargoDestinationsInfo } from "@/widgets/offer-cargo-destinations-info";
import { OfferCargoOfficeInfo } from "@/widgets/offer-cargo-office-info";
import { OfferCargoShippingFeeInfo } from "@/widgets/offer-cargo-shipping-fee-info";
import { OfferCargoStatusCard } from "@/widgets/offer-cargo-status-card/ui";
import { OfferCargoTaskCard } from "@/widgets/offer-cargo-task-card";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import type React from "react";
import { useMemo, useState } from "react";
import { OfferCargoReceptionDeadlineInfo } from "../../../widgets/offer-cargo-reception-deadline-info";
import { OfferCargoScheduleInfo } from "../../../widgets/offer-cargo-schedule-info";
import type { ShipperCargoDetailPageProps } from "../model";
import { DeliveryProposalsInfo } from "./delivery-proposals-info";
import { DeliveryRequestFileUploader } from "./delivery-request-file-uploader";
import { ScheduledCarrierInfo } from "./scheduled-carrier-info";

export const ShipperCargoDetailPageUi: React.FC<ShipperCargoDetailPageProps> = (
  props,
) => {
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelConfirmModalOpen, setIsCancelConfirmModalOpen] =
    useState(false);
  const navigate = useNavigate({ from: "/shipper/cargos/$cargoId" });

  const links = [
    { href: "/shipper/cargos", label: "マイ荷物" },
    { label: "荷物詳細" },
  ];

  const offerCargoProcess: Loadable<OfferCargoProcess> =
    props.offerCargoProcess;
  const offerCargoProcessStatus = useMemo(() => {
    return transformLoadable(offerCargoProcess, (process) => {
      return calculateOfferCargoProcessStatus({
        process,
      });
    });
  }, [offerCargoProcess]);

  const latestOnGoingDeliveryProposalOfferCargo = props.offerCargoProcess.data
    ? calculateLatestOnGoingDeliveryProposalOfferCargo({
        offerCargoProcess: props.offerCargoProcess.data,
      })
    : undefined;
  const latestValidContractOfferCargo =
    props.offerCargoProcess.data && latestOnGoingDeliveryProposalOfferCargo
      ? calculateLatestValidContractOfferCargo({
          deliveryProposalOfferCargoId:
            latestOnGoingDeliveryProposalOfferCargo.id,
          offerCargoProcess: props.offerCargoProcess.data,
        })
      : undefined;
  const contractConcludedOfferCargo =
    props.offerCargoProcess.data?.contractOfferCargos.find(
      (cargo) => cargo.stage === "contract_concluded",
    );

  const STEPPER_LIST = ["荷物登録", "配送提案", "配送確認"];
  const currentStep = useMemo(() => {
    switch (offerCargoProcessStatus.data) {
      case "no_valid_delivery_proposals":
      case "reception_suspended":
      case "proposal_reception_deadline_expired":
        return 0;
      case "has_ongoing_proposal_with_no_contract":
      case "has_contracts_all_rejected":
        return 1;
      case "has_contract_concluded":
        return 2;
      default:
        return -1;
    }
  }, [offerCargoProcessStatus]);

  const showDeliveryProposalListOnTop =
    !offerCargoProcessStatus.data ||
    offerCargoProcessStatus.data === "no_valid_delivery_proposals" ||
    offerCargoProcessStatus.data === "has_ongoing_proposal_with_no_contract" ||
    offerCargoProcessStatus.data === "reception_suspended" ||
    offerCargoProcessStatus.data === "proposal_reception_deadline_expired";
  const showSuspendProcessButton =
    offerCargoProcessStatus.data === "no_valid_delivery_proposals" ||
    offerCargoProcessStatus.data === "has_ongoing_proposal_with_no_contract" ||
    offerCargoProcessStatus.data === "has_contracts_all_rejected";

  return (
    <Box px={{ xs: 2, sm: 4 }} pb={10}>
      <ScrollToTop />
      <BaseBreadCrumb links={links} sx={{ mb: 3 }} />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          荷物詳細
        </Typography>
        <BaseButton
          startIcon={<BaseIcon name="plus" htmlColor="#52A48B" />}
          variant="outlined"
          onClick={props.onCopyCargoClick}
        >
          コピーして荷物登録する
        </BaseButton>
      </Box>
      {offerCargoProcess.status === "loading" ||
      offerCargoProcessStatus.status === "loading" ? (
        <Stack width="100%" justifyContent="center" alignItems="center" py={18}>
          <CircularProgress />
        </Stack>
      ) : offerCargoProcess.status === "error" ||
        offerCargoProcessStatus.status === "error" ? (
        <Stack width="100%" justifyContent="center" alignItems="center" py={18}>
          <Typography variant="body1" color="error">
            エラーが発生しました
          </Typography>
          <Typography variant="caption" color="error">
            {offerCargoProcess.error?.message}
          </Typography>
        </Stack>
      ) : (
        <Stack mt={{ xs: 3, sm: 4 }} spacing={{ xs: 3, sm: 4 }}>
          <BaseStepper currentStep={currentStep} stepperList={STEPPER_LIST} />
          <Stack spacing={1} alignSelf={{ xs: "stretch", sm: "start" }}>
            <OfferCargoStatusCard
              variant="shipper_cargo_detail"
              offerCargoProcess={offerCargoProcess.data}
              onRecreateContractClick={props.onRecreateContractClick}
            />
            <OfferCargoTaskCard
              viewer="shipper"
              offerCargoProcess={offerCargoProcess.data}
              onShowDeliveryProposalListClick={() =>
                navigate({ hash: "delivery-proposal-list", replace: true })
              }
              onShowDeliveryRequestUploaderClick={() =>
                navigate({ hash: "delivery-request-uploader", replace: true })
              }
            />
          </Stack>
          <BaseDivider />
          {showDeliveryProposalListOnTop && (
            <>
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <DeliveryProposalsInfo
                  offerCargoProcess={offerCargoProcess}
                  onReject={props.onDeliveryProposalReject}
                />
              </Box>
              <Box sx={{ display: { xs: "block", sm: "none" } }}>
                <MobileDeliveryProposalList
                  offerCargoProcess={offerCargoProcess}
                  onReject={props.onDeliveryProposalReject}
                />
              </Box>
              <BaseDivider />
            </>
          )}
          {contractConcludedOfferCargo && (
            <>
              <Box id="delivery-request-uploader" maxWidth="628px">
                <DeliveryRequestFileUploader
                  files={
                    contractConcludedOfferCargo.attachedDeliveryRequestFiles?.map(
                      (file) => ({
                        url: file.url || "",
                        filename: file.filename || "",
                      }),
                    ) || []
                  }
                  onFileUpload={props.onDeliveryRequestFilesSubmit}
                  onFileDelete={props.onDeliveryRequestFileDelete}
                  isUploading={props.isDeliveryRequestFilesUploading}
                />
              </Box>
              <ScheduledCarrierInfo
                scheduledCarrier={contractConcludedOfferCargo.scheduledCarrier}
              />
              <BaseDivider />
            </>
          )}
          <Stack spacing={3} sx={{ maxWidth: "628px" }}>
            <Typography variant="h6" fontWeight={700}>
              {!latestValidContractOfferCargo ? "荷物登録内容" : "契約内容"}
            </Typography>
            <OfferCargoShippingFeeInfo
              viewer="shipper"
              offerCargoProcess={offerCargoProcess.data}
              deliveryProposalOfferCargoId={
                latestOnGoingDeliveryProposalOfferCargo?.id
              }
            />
            {showDeliveryProposalListOnTop && (
              <OfferCargoScheduleInfo
                draftOfferCargo={offerCargoProcess.data.draftOfferCargo}
              />
            )}
            {!latestValidContractOfferCargo ? (
              <OfferCargoDestinationsInfo
                variant="shipper_cargo_detail"
                draftOfferCargo={offerCargoProcess.data.draftOfferCargo}
              />
            ) : (
              <OfferCargoDestinationsInfo
                variant="shipper_contract_detail"
                draftOfferCargo={offerCargoProcess.data.draftOfferCargo}
                contractOfferCargo={latestValidContractOfferCargo}
              />
            )}
            <OfferCargoCargoInfo
              baseOfferCargo={
                latestValidContractOfferCargo ??
                offerCargoProcess.data.draftOfferCargo
              }
              viewer="shipper"
            />
            <OfferCargoOfficeInfo
              variant="shipper_cargo_detail"
              offerCargoProcess={offerCargoProcess.data}
              deliveryProposalOfferCargo={
                latestOnGoingDeliveryProposalOfferCargo
              }
            />
          </Stack>
          <BaseDivider />
          {!showDeliveryProposalListOnTop && (
            <>
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                <DeliveryProposalsInfo
                  offerCargoProcess={offerCargoProcess}
                  onReject={props.onDeliveryProposalReject}
                />
              </Box>
              <Box sx={{ display: { xs: "block", sm: "none" } }}>
                <MobileDeliveryProposalList
                  offerCargoProcess={offerCargoProcess}
                  onReject={props.onDeliveryProposalReject}
                />
              </Box>
              <BaseDivider />
            </>
          )}
          {offerCargoProcess.status === "success" && (
            <OfferCargoReceptionDeadlineInfo
              draftOfferCargo={offerCargoProcess.data.draftOfferCargo}
            />
          )}
          {showSuspendProcessButton && (
            <>
              <BaseButton
                variant="outlined"
                color="warning"
                sx={{ fontSize: "14px", maxWidth: "300px", fontWeight: 700 }}
                startIcon={<BaseIcon name="minusCircle" htmlColor="#EA583B" />}
                onClick={() => setIsCancelModalOpen(true)}
              >
                この荷物の配送提案を受付停止する
              </BaseButton>
              <BaseActionableDialog
                title="配送提案の受付を停止します"
                open={isCancelModalOpen}
                actionButtonLabel="停止する"
                actionButtonDisabled={!showSuspendProcessButton}
                cancelButtonLabel="キャンセル"
                onClose={() => setIsCancelModalOpen(false)}
                onSubmit={() => props.onProcessSuspend()}
              >
                <Stack spacing={2} mb={4}>
                  <Box>
                    <Typography variant="body2" fontWeight={400}>
                      停止すると、配送業者から新規の提案が届かなくなり、契約の作成もできません。
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={400}
                      color="#EA583B"
                    >
                      また、停止した場合、再開はできません。
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight={400}>
                      ※ 受付停止は下記を満たした場合のみ可能です。
                    </Typography>
                    <Typography variant="body2" fontWeight={400}>
                      <b>・</b>
                      配送業者からの提案が0件、あるいは全ての提案が「見送り」されている
                    </Typography>
                    <Typography variant="body2" fontWeight={400}>
                      <b>・</b>契約が作成されていない
                    </Typography>
                  </Box>
                </Stack>
              </BaseActionableDialog>
              <BaseActionableDialog
                title="配送提案の受付を停止しました"
                open={isCancelConfirmModalOpen}
                onClose={() => setIsCancelConfirmModalOpen(false)}
              />
            </>
          )}
        </Stack>
      )}
    </Box>
  );
};
